import type { FC, ReactNode } from 'react'
import type { DiagnosisLevel } from '@/types'

import { useRef, useState } from 'react'
import { ArrowUpLeft, Phone, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRange, pad } from '@/lib/format'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'
import { ButtonLink } from '@/components/generals/button'
import { diagnoses, getDiagnosisById, DIAGNOSIS_FINEPRINT } from '@/constants/diagnosis'
import { siteConfig, QUOTE_PATH } from '@/constants/site'

const levelStyles: Record<DiagnosisLevel, { dot: string, label: string }> = {
    high: { dot: 'bg-ember-soft', label: "Act today" },
    medium: { dot: 'bg-amber-soft', label: "Book soon" },
    low: { dot: 'bg-moss-soft', label: "Not urgent" }
}

export const DiagnosisPanel: FC = (): ReactNode => {
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const resultRef = useRef<HTMLDivElement>(null)
    const selected = selectedId ? getDiagnosisById(selectedId) : undefined

    useGSAP(() => {
        const element = resultRef.current

        if (!element || !selectedId) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            gsap.fromTo(
                element.querySelectorAll('[data-animate]'),
                { y: 22, autoAlpha: 0 },
                { y: 0, autoAlpha: 1, duration: 0.8, ease: 'expo.out', stagger: 0.05 }
            )
        })

        return () => media.revert()
    }, { scope: resultRef, dependencies: [selectedId], revertOnUpdate: true })

    return (
        <div className='grid gap-6 lg:grid-cols-12 lg:gap-8'>
            <div role='radiogroup' aria-label="What is your door doing?" className='flex flex-col gap-2 lg:col-span-7'>
                {diagnoses.map((diagnosis, index) => {
                    const active = diagnosis.id === selectedId

                    return (
                        <button
                            key={diagnosis.id}
                            type='button'
                            role='radio'
                            aria-checked={active}
                            onClick={() => setSelectedId(diagnosis.id)}
                            className={cn(
                                'group flex items-center gap-5 rounded-[22px] border px-5 py-4 text-left transition-[background-color,border-color,color,transform,translate,scale,rotate] duration-300 sm:px-6 sm:py-5',
                                active
                                    ? 'border-ink bg-ink text-cream'
                                    : 'border-ink/[0.08] bg-card hover:-translate-y-0.5 hover:border-ink/20'
                            )}
                        >
                            <span className={cn('font-mono text-[12px] transition-colors', active ? 'text-ember-soft' : 'text-slate')}>
                                {pad(index + 1)}
                            </span>

                            <span className='min-w-0 flex-1'>
                                <span className='block font-heading text-[1.2rem] font-semibold tracking-[-0.025em] sm:text-[1.35rem]'>
                                    {diagnosis.title}
                                </span>

                                <span className={cn('mt-1 block text-[14.5px] leading-snug', active ? 'text-cream/65' : 'text-slate')}>
                                    {diagnosis.description}
                                </span>
                            </span>

                            <span
                                aria-hidden='true'
                                className={cn(
                                    'flex size-7 shrink-0 items-center justify-center rounded-full border transition-colors',
                                    active ? 'border-ember bg-ember' : 'border-ink/20 group-hover:border-ink/40'
                                )}
                            >
                                <span className={cn('size-2 rounded-full bg-cream transition-transform duration-300', active ? 'scale-100' : 'scale-0')} />
                            </span>
                        </button>
                    )
                })}
            </div>

            <div className='lg:col-span-5'>
                <div
                    ref={resultRef}
                    aria-live='polite'
                    className='relative min-h-[26rem] overflow-hidden rounded-[30px] bg-ink p-7 text-cream sm:p-9 lg:sticky lg:top-28'
                >
                    <div className='door-grooves pointer-events-none absolute inset-0 opacity-50' aria-hidden='true' />
                    <div className='pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-ember/25 blur-[90px]' aria-hidden='true' />

                    {!selected && (
                        <div className='relative flex h-full min-h-[21rem] flex-col justify-between'>
                            <span className='flex size-12 items-center justify-center rounded-full border border-cream/20'>
                                <ArrowUpLeft className='size-5 text-ember-soft' aria-hidden='true' />
                            </span>

                            <p className='max-w-[18ch] font-heading text-[1.9rem] font-semibold leading-[1.08] tracking-[-0.035em] text-cream/90'>
                                Pick a symptom. The likely fix, how soon to act and a typical price appear here.
                            </p>
                        </div>
                    )}

                    {selected && (
                        <div className='relative'>
                            <div data-animate className='flex flex-wrap items-center gap-3'>
                                <span className='inline-flex items-center gap-2 rounded-full bg-cream/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em]'>
                                    <span className={cn('size-2 rounded-full', levelStyles[selected.level].dot)} />
                                    {levelStyles[selected.level].label}
                                </span>

                                <span className='text-sm text-cream/70'>{selected.urgency}</span>
                            </div>

                            <p data-animate className='mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-cream/45'>
                                What it usually needs
                            </p>

                            <p data-animate className='mt-3 font-heading text-[1.55rem] font-semibold leading-[1.15] tracking-[-0.03em]'>
                                {selected.fix}
                            </p>

                            <div data-animate className='mt-8 flex items-end justify-between gap-4 border-t border-cream/12 pt-6'>
                                <div>
                                    <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/45'>
                                        Usual range
                                    </p>

                                    <p className='mt-2 font-heading text-[2.4rem] font-semibold leading-none tracking-[-0.04em]'>
                                        {formatRange(selected.lo, selected.hi)}
                                    </p>
                                </div>
                            </div>

                            {selected.safety && (
                                <p data-animate className='mt-6 flex gap-3 rounded-2xl bg-ember/15 p-4 text-[14px] leading-relaxed text-cream/85'>
                                    <TriangleAlert className='mt-0.5 size-4 shrink-0 text-ember-soft' aria-hidden='true' />
                                    {selected.safety}
                                </p>
                            )}

                            <div data-animate className='mt-8 flex flex-wrap gap-3'>
                                <ButtonLink href={`${QUOTE_PATH}?diagnosis=${selected.id}&entry=diagnosis`} size='md'>
                                    Quote this fix
                                </ButtonLink>

                                <ButtonLink href={siteConfig.phoneHref} variant='outline-light' size='md' icon={Phone}>
                                    Call us
                                </ButtonLink>
                            </div>

                            <p data-animate className='mt-6 text-[12.5px] leading-relaxed text-cream/45'>
                                {DIAGNOSIS_FINEPRINT}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
